/**
 * Geometry helpers ported from the concept renderer (concept L221–L367): part merging with per-part colour,
 * deterministic vertex jitter, instancing and seeded scattering along the river.
 */
import {
  LatheGeometry,
  Path,
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  Color,
  Euler,
  InstancedMesh,
  Material,
  Matrix4,
  Mesh,
  Object3D,
  Quaternion,
  Vector3,
} from 'three';
import { fract, lerp, PERIOD } from '../core/math.ts';
import type { Rng } from '../core/rng.ts';
import type { WorldProfile } from './profiles.ts';

export interface Part {
  geo: BufferGeometry;
  color?: Color;
}

const WHITE = new Color(1, 1, 1);

/** A geometry attribute that must exist (position, normal). */
export function attr(g: BufferGeometry, name: string): BufferAttribute {
  const a = g.getAttribute(name);
  if (!(a instanceof BufferAttribute)) throw new Error(`geometry has no ${name} attribute`);
  return a;
}

/** Merges parts into one non-indexed geometry with a per-vertex colour per part (concept L223). */
export function merge(parts: Part[]): BufferGeometry {
  const list: { g: BufferGeometry; c: Color }[] = [];
  let total = 0;
  for (const part of parts) {
    let g = part.geo;
    if (g.index) g = g.toNonIndexed();
    if (!g.attributes.normal) g.computeVertexNormals();
    list.push({ g, c: part.color ?? WHITE });
    total += attr(g, 'position').count;
  }
  const P = new Float32Array(total * 3);
  const N = new Float32Array(total * 3);
  const C = new Float32Array(total * 3);
  let o = 0;
  for (const e of list) {
    const pos = attr(e.g, 'position');
    const n = pos.count;
    P.set((pos.array as Float32Array).subarray(0, n * 3), o * 3);
    N.set((attr(e.g, 'normal').array as Float32Array).subarray(0, n * 3), o * 3);
    for (let k = 0; k < n; k++) {
      const j = (o + k) * 3;
      C[j] = e.c.r;
      C[j + 1] = e.c.g;
      C[j + 2] = e.c.b;
    }
    o += n;
  }
  const out = new BufferGeometry();
  out.setAttribute('position', new BufferAttribute(P, 3));
  out.setAttribute('normal', new BufferAttribute(N, 3));
  out.setAttribute('color', new BufferAttribute(C, 3));
  out.computeBoundingSphere();
  return out;
}

/** Deterministic per-vertex displacement from a sine hash (concept L246). */
export function jitter(geo: BufferGeometry, amt: number, seed: number): BufferGeometry {
  const p = attr(geo, 'position');
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i);
    const y = p.getY(i);
    const z = p.getZ(i);
    const h = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719 + seed) * 43758.5453;
    p.setXYZ(i, x + (fract(h) - 0.5) * amt, y + (fract(h * 1.37) - 0.5) * amt, z + (fract(h * 1.93) - 0.5) * amt);
  }
  geo.computeVertexNormals();
  return geo;
}

export function box(w: number, h: number, d: number, x = 0, y = 0, z = 0, color?: Color): Part {
  const g = new BoxGeometry(w, h, d);
  g.translate(x, y, z);
  return { geo: g, color };
}

export function mesh(geo: BufferGeometry, mat: Material, x = 0, y = 0, z = 0, parent?: Object3D, cast = true): Mesh {
  const m = new Mesh(geo, mat);
  m.position.set(x, y, z);
  m.castShadow = cast;
  m.receiveShadow = true;
  if (parent) parent.add(m);
  return m;
}

export interface InstanceItem {
  x: number;
  y: number;
  z: number;
  sx: number;
  sy: number;
  sz: number;
  rot?: number;
  lean?: number;
  lean2?: number;
  r: number;
  g: number;
  b: number;
}

/** One InstancedMesh with per-instance transform and colour (concept L342). */
export function instanced(geo: BufferGeometry, mat: Material, items: InstanceItem[], cast: boolean): InstancedMesh {
  const m = new InstancedMesh(geo, mat, Math.max(items.length, 1));
  const q = new Quaternion();
  const e = new Euler();
  const s = new Vector3();
  const p = new Vector3();
  const M = new Matrix4();
  const c = new Color();
  items.forEach((it, i) => {
    e.set(it.lean ?? 0, it.rot ?? 0, it.lean2 ?? 0);
    q.setFromEuler(e);
    p.set(it.x, it.y, it.z);
    s.set(it.sx, it.sy, it.sz);
    M.compose(p, q, s);
    m.setMatrixAt(i, M);
    c.setRGB(it.r, it.g, it.b);
    m.setColorAt(i, c);
  });
  m.count = items.length;
  m.castShadow = cast;
  m.receiveShadow = true;
  m.frustumCulled = false;
  m.instanceMatrix.needsUpdate = true;
  if (m.instanceColor) m.instanceColor.needsUpdate = true;
  return m;
}

export type Accept = (x: number, z: number, rand: Rng) => InstanceItem | null;

/**
 * Scatters up to n accepted items over one 800 m period, then copies them to the neighbouring periods that
 * fall inside [z1, z0] (concept L355). Consumes `rand` in exactly the concept's order.
 */
export function scatter(
  W: WorldProfile,
  rand: Rng,
  n: number,
  range: number,
  accept: Accept,
  z0: number,
  z1: number,
): InstanceItem[] {
  const base: InstanceItem[] = [];
  let tries = 0;
  while (base.length < n && tries < n * 40) {
    tries++;
    const z = -rand() * PERIOD;
    const x = W.center(z) + (rand() * 2 - 1) * range;
    const it = accept(x, z, rand);
    if (it) base.push(it);
  }
  const out: InstanceItem[] = [];
  for (const off of [PERIOD, 0, -PERIOD])
    for (const b of base) {
      const zz = b.z + off;
      if (zz < z0 && zz > z1) out.push({ ...b, z: zz });
    }
  return out;
}

export type RibbonFn = (u: number, c: number, w: number, z: number, out: Color) => [number, number, number, number];

/** A strip that follows the river: `fn` maps a column (u in 0..1) at z to [x, y, uvx, uvy] and a colour (concept L290). */
export function buildRibbon(
  W: WorldProfile,
  z0: number,
  z1: number,
  nz: number,
  cols: number,
  fn: RibbonFn,
): BufferGeometry {
  const nv = (cols + 1) * (nz + 1);
  const pos = new Float32Array(nv * 3);
  const uv = new Float32Array(nv * 2);
  const cl = new Float32Array(nv * 3);
  const tmp = new Color();
  let i = 0;
  for (let iz = 0; iz <= nz; iz++) {
    const z = lerp(z0, z1, iz / nz);
    const c = W.center(z);
    const w = W.width(z);
    for (let ix = 0; ix <= cols; ix++) {
      const r = fn(ix / cols, c, w, z, tmp);
      pos[i * 3] = r[0];
      pos[i * 3 + 1] = r[1];
      pos[i * 3 + 2] = z;
      uv[i * 2] = r[2];
      uv[i * 2 + 1] = r[3];
      cl[i * 3] = tmp.r;
      cl[i * 3 + 1] = tmp.g;
      cl[i * 3 + 2] = tmp.b;
      i++;
    }
  }
  const idx: number[] = [];
  for (let iz = 0; iz < nz; iz++)
    for (let ix = 0; ix < cols; ix++) {
      const a = iz * (cols + 1) + ix;
      const b = a + 1;
      const cc = a + cols + 1;
      const dd = cc + 1;
      idx.push(a, b, cc, b, dd, cc);
    }
  const geo = new BufferGeometry();
  geo.setAttribute('position', new BufferAttribute(pos, 3));
  geo.setAttribute('uv', new BufferAttribute(uv, 2));
  geo.setAttribute('color', new BufferAttribute(cl, 3));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  geo.computeBoundingSphere();
  return geo;
}

/**
 * The concept's capsule: three.js r159's CapsuleGeometry was a lathed path. r186 rebuilt the class with a different
 * tessellation (210 vs 390 vertices for the jet spine), so the concept's shape is reproduced from its r159
 * definition on r186's LatheGeometry.
 */
export function conceptCapsule(
  radius: number,
  length: number,
  capSegments: number,
  radialSegments: number,
): LatheGeometry {
  const path = new Path();
  path.absarc(0, -length / 2, radius, Math.PI * 1.5, 0, false);
  path.absarc(0, length / 2, radius, 0, Math.PI * 0.5, false);
  return new LatheGeometry(path.getPoints(capSegments), radialSegments);
}
